import React, { useEffect, useState } from 'react';
import { toast } from "sonner";
import { useParams } from "react-router-dom";
import { ArrowLeft, Check, Circle, FileText } from "lucide-react";

import { cn } from "@/lib/utils.ts";
import { Button } from "@/components/ui/button.tsx";
import { PHQ9_DATA, GAD7_DATA } from "@/lib/questions.ts";
import { Separator } from "@/components/ui/separator.tsx";
import AuthLayout from "@/components/layout/AuthLayout.tsx";
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card.tsx';
import AuthService from "@/lib/auth";
import EmailService from "@/lib/email";

const TokenQuestionnaire: React.FC = () => {
  const { token } = useParams();
  const [page, setPage] = useState(1);
  const [questions, setQuestions] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [questionnaire, setQuestionnaire] = useState<any>({});
  const [currentStep, setCurrentStep] = useState<"questionnaire" | "review" | "complete">("questionnaire");

  async function getTokenQuestionnaire(_qt: string) {
    try {
      const qr: any = await AuthService.getTokenQuestionnaire(_qt);

      if (qr) {
        setQuestionnaire(qr);
        if (qr.questionnaire_type === "PHQ-9") {
          setQuestions(PHQ9_DATA);
        } else if (qr.questionnaire_type === "GAD-7") {
          setQuestions(GAD7_DATA);
        }
      }
    } catch (error) {
      console.error("Error fetching token questionnaire:", error);
    }
  }


  useEffect(() => {
    if (token !== undefined && token?.length > 0) {
      console.log(token);
      (async () => {
        await getTokenQuestionnaire(token);
      })();

    }
  }, [token]);


  const reset_page = () => {
    setCurrentStep("questionnaire");
    setPage(1);

  }

  const canContinue = () => {
    return questions.every((q) => q.selected !== null && q.selected !== undefined);
  };

  const handleResponse = (questionId: number, value: number) => {
    setQuestions((prev) =>
      prev.map((q) =>
        q.id === questionId ? { ...q, selected: value } : q
      )
    );
  };

  const handleFormSubmit = async () => {
    setLoading(true);
    try {
      const totalScore = questions.reduce((sum, q) => {
        return sum + (typeof q.selected === "number" ? q.selected : 0);
      }, 0);

      await AuthService.updateQuestionnaireResponse(questionnaire.id, {
        answers: JSON.stringify(questions),
        total_score: totalScore,
        self_reported: true,
        notes: "Questionnaire responses reported from sms url",
        status: 'pending for review'
      });

      // Fetch patient details using AuthService
      const patient = await AuthService.getPatient(questionnaire.patient_id);

      if (patient) {
        const { error } = await EmailService.sendEmail({
          to: patient.email || "test@example.com",
          subject: `Health Report: ${questionnaire.questionnaire_type} - ${patient.first_name}`,
          body: `
            <div style="font-family:sans-serif;max-width:600px;margin:auto;border:1px solid #eee;padding:20px;">
              <h2 style="color:#16a34a;">Assessment Completed</h2>
              <p>Hello ${patient.first_name},</p>
              <p>Thank you for completing the <b>${questionnaire.questionnaire_type}</b> assessment.</p>
              <hr style="border:none;border-top:1px solid #eee;margin:20px 0;"/>
              <p style="font-size:16px;"><b>Total Score:</b> ${totalScore}</p>
              <p style="color:#666;font-size:14px;">Your responses have been securely transmitted to your healthcare provider for review.</p>
            </div>`,
        });

        if (!error) {
          setCurrentStep("complete");
        }
      }
    } catch (error) {
      toast.error(`Error saving questionnaire responses. Please try again.`);
      console.error(error);
    }
    finally {
      setLoading(false);
    }
  };

  const renderQuestionnaire = () => {

    return (
      <Card className="w-full bg-white shadow-md rounded-2xl overflow-hidden border border-gray-200 py-0">
        <CardHeader className="bg-gradient-to-r from-green-400 to-lime-200 p-6 rounded-t-2xl">
          <div className="flex items-center mb-4">
            <FileText className="mr-3 text-white" size={28} />
            <CardTitle className="text-3xl font-bold text-white">
              {questionnaire.questionnaire_type} Questionnaire
            </CardTitle>
          </div>
          <div className="w-full bg-white/30 rounded-full h-2">
            <div
              style={{
                width: page === 1 ? "50%" : "100%",
              }}
              className="h-2 rounded-full bg-gradient-to-r from-green-400 via-yellow-300 to-green-100 transition-all duration-300"
            ></div>
          </div>
          <p className="text-sm text-white mt-2">
            Page {page} of 2 - Over the last 2 weeks, how often have you been bothered by any of the following problems?
          </p>
        </CardHeader>

        <CardContent className="space-y-6 p-6">
          {questions.map((q, index) => {
            return (
              <div key={index} className="bg-gradient-to-br from-green-50 via-yellow-50 to-green-100 p-5 rounded-xl border border-green-200 shadow-sm">
                <p className="font-semibold text-gray-700 mb-4 text-lg">
                  {index + 1}. {q.question}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
                  {q.options.map((option: any) => {
                    const isSelected = q.selected === option.value;
                    return (
                      <div
                        key={option.value}
                        onClick={() => handleResponse(q.id, option.value)}
                        className={`flex items-center justify-start gap-2 px-4 py-3 rounded-lg cursor-pointer border transition-all duration-200 text-center text-sm font-medium ${isSelected
                          ? "bg-green-100 border-green-600 text-green-800 shadow-inner ring-2 ring-green-500"
                          : "bg-white border-gray-200 hover:bg-gray-50"
                          }`}
                      >
                        {isSelected ? (
                          <Check className="text-green-600" size={18} />
                        ) : (
                          <Circle className="text-gray-400" size={18} />
                        )}
                        <span>{option.label}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </CardContent>

        <div className="flex flex-col md:flex-row md:justify-end p-6 pt-0 gap-4">
          <Button
            onClick={handleFormSubmit}
            disabled={loading || !canContinue()}
            className="w-full h-15 md:w-auto bg-green-600 hover:bg-green-700 text-white"
          >
            {loading ? (
              <div className="spinner h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <>
                Submit <Check className="ml-2" size={16} />
              </>
            )}
          </Button>
        </div>
      </Card>

    );
  };


  const renderReview = () => {
    const totalScore = questions.reduce((sum, q) => {
      return sum + (typeof q.selected === "number" ? q.selected : 0);
    }, 0);

    return (
      <Card className="max-w-4xl mx-auto bg-white shadow-md rounded-2xl overflow-hidden border border-gray-200 py-0">
        <CardHeader className="bg-gradient-to-r from-green-400 to-lime-200 p-6 rounded-t-2xl">
          <CardTitle className="text-3xl font-bold text-white mb-2">Review Your Answers</CardTitle>
          <div className="w-full bg-white/30 rounded-full h-2">
            <div className="bg-gradient-to-r from-green-400 via-yellow-300 to-green-100 h-2 rounded-full w-full"></div>
          </div>
        </CardHeader>

        <CardContent className="space-y-6 p-6">
          {questions.map((q, index) => (
            <div
              key={index}
              className="bg-gradient-to-br from-green-50 via-yellow-50 to-green-100 p-5 rounded-xl border border-green-200 shadow-sm"
            >
              <p className="font-semibold text-gray-700 mb-2 text-lg">
                {index + 1}. {q.question}
              </p>
              <p className="text-green-700 font-medium text-base">
                {q.options.find((opt: any) => opt.value === q.selected)?.label || "Not answered"}
              </p>
            </div>
          ))}

          <Separator />

          <div className="bg-blue-50 p-5 rounded-xl shadow-sm border border-blue-200">
            <p className="font-semibold text-blue-800 text-lg">
              Total Score: {totalScore}/27
            </p>
          </div>
        </CardContent>

        <div className="flex flex-col md:flex-row justify-between gap-4 p-6 pt-0">
          <Button
            onClick={() => setCurrentStep("questionnaire")}
            variant="outline"
            className="w-full md:w-auto bg-white border-gray-300 hover:bg-gray-100 text-gray-700"
          >
            <ArrowLeft className="mr-2" size={16} />
            Edit Answers
          </Button>

          <Button
            onClick={handleFormSubmit}
            disabled={loading}
            className="w-full md:w-auto bg-green-600 hover:bg-green-700 text-white"
          >
            {loading ? (
              <div className="spinner h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <>
                Submit Final Answers <Check className="ml-2" size={16} />
              </>
            )}
          </Button>
        </div>
      </Card>

    );
  };

  const renderComplete = () => (
    <Card className="max-w-2xl mx-auto text-center bg-white shadow-md rounded-2xl border border-gray-200 overflow-hidden py-0">
      <CardHeader className="bg-gradient-to-r from-green-400 to-lime-200 p-6 rounded-t-2xl">
        <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-md">
          <Check className="text-green-600" size={32} />
        </div>
        <CardTitle className="text-2xl font-bold text-white mb-2">Form Completed Successfully</CardTitle>
        <p className="text-white/90 text-sm">
          Thank you for completing the patient intake form and ${questionnaire.questionnaire_type} questionnaire.
        </p>
      </CardHeader>

      <CardContent className="p-6 space-y-6">
        <div className="bg-green-50 p-4 rounded-xl border border-green-200 shadow-sm text-left">
          <p className="text-sm text-green-800">
            <strong>Patient ID:</strong> {questionnaire.patient_id}
          </p>
          <p className="text-sm text-green-800">
            Your information has been securely saved and will be reviewed by healthcare staff.
          </p>
          <p className="text-sm text-red-500">
            You can close this page now.
          </p>
        </div>

        <Button
          onClick={() => reset_page()}
          className={cn(
            "w-full mt-4 h-15 text-lg font-bold text-white",
            "bg-gradient-to-r from-green-400 to-green-500",
            "rounded-full shadow-lg shadow-green-300/40",
            "hover:scale-[1.02] transition-transform duration-150 ease-in-out",
            "disabled:opacity-60 disabled:cursor-not-allowed",
            "flex items-center justify-center"
          )}
        >
          Re-Record Response
        </Button>
      </CardContent>
    </Card>
  );


  return (<AuthLayout >
    <div className="min-h-screen bg-gray-100 py-8 px-4"> {/* Added px-4 for mobile padding */}
      {currentStep === "questionnaire" && renderQuestionnaire()}
      {currentStep === "review" && renderReview()}
      {currentStep === "complete" && renderComplete()}
    </div>
  </AuthLayout>);
};

export default TokenQuestionnaire;
